/**
 * Module dependencies.
 */

import { GetStaticProps, NextPage } from 'next/types';
import { LayoutProps } from 'src/components/layout';
import { theme as amtrolAlfaTheme } from 'src/styles/theme';
import { bannersKeys } from 'src/core/content-config/banners';
import { contentConfig } from 'src/core/content-config/about-us';
import { fetchBanner } from 'src/api/banners/fetch-banner';
import AwardsSection from 'src/components/about-us/awards-section';
import BannerSection, { Banner } from 'src/components/sections/banner-section';
import HistoryTeamSection from 'src/components/about-us/history-team-section';
import Metatags from 'src/components/core/metatags';
import SustainabilitySection from 'src/components/about-us/sustainability-section';
import TextImageSection from 'src/components/sections/text-image-section';
import styled from 'styled-components';
import useBanner from 'src/api/banners/use-banner';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  banners: Banner[];
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  position: relative;
`;

/**
 * `AboutUs` page.
 */

const AboutUs: NextPage<Props> = props => {
  const { translate } = useTranslate();
  const { data: bannersData, isSuccess: isSuccessBanners } = useBanner({
    initialData: props?.banners,
    slug: bannersKeys.aboutUs
  });

  return (
    <>
      <Metatags
        description={translate('about-us:page.description')}
        pageTitle={translate('about-us:page.title')}
        title={translate('about-us:page.title')}
      />

      <Wrapper>
        {isSuccessBanners && <BannerSection banners={bannersData} />}

        <TextImageSection
          bgColor={amtrolAlfaTheme.colors.backgrounds.sage01}
          blocks={contentConfig.innovationBlocks}
          description={translate('about-us:sections.manufacture.description')}
          imageUrl={'/static/images/lab.jpg'}
          subtitleOne={translate('about-us:sections.manufacture.subtitleOne')}
          subtitleTwo={translate('about-us:sections.manufacture.subtitleTwo')}
          titleOne={translate('about-us:sections.manufacture.titleOne')}
          titleTwo={translate('about-us:sections.manufacture.titleTwo')}
        />

        <AwardsSection />

        <SustainabilitySection />

        <HistoryTeamSection />
      </Wrapper>
    </>
  );
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<Props & LayoutProps> = async ({
  locale
}) => {
  const banners = await fetchBanner({ locale, slug: bannersKeys.aboutUs });

  return {
    props: {
      banners,
      hasMobileScrollDownIndicator: true,
      hasScrollDownIndicator: true
    },
    revalidate: 60 * 60 * 24
  };
};

/**
 * Export `AboutUs` page.
 */

export default AboutUs;
